import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FeedbackKind, type FeedbackStatus, FEEDBACK_STATUSES, useMyFeedback } from '../api/feedback';
import { useReadOnly } from '../auth/session';
import { FeedbackDialog, statusChipClass } from '../components/FeedbackDialog';
import { FilterChips } from '../components/FilterChips';
import { ErrorView } from '../components/Guards';
import { dateLocale } from '../i18n';
import { formatDate } from '../lib/format';
import ui from '../styles/ui.module.css';
import styles from './MyFeedbackPage.module.css';

/**
 * "Bildirimlerim" (/feedback): the user's own problem reports and suggestions, newest first, with
 * the status and the administrator's answer, like the other listing pages. "+ Yeni bildirim"
 * opens the same dialog as the header's menu.
 */
export function MyFeedbackPage() {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const mine = useMyFeedback(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [status, setStatus] = useState<FeedbackStatus | null>(null);
  const all = mine.data ?? [];
  const ofKind = all.filter((f) => kind === null || f.kind === kind);
  const shown = ofKind.filter((f) => status === null || f.status === status);
  const locale = dateLocale();

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('myFeedback.title')}</h1>
          <div className={ui.subtitle}>{t('myFeedback.subtitle')}</div>
          <div className={ui.subtitle}>{t('myFeedback.retention')}</div>
        </div>
        <button type="button" className={`${ui.button} ${ui.primary}`} disabled={readOnly} onClick={() => setDialogOpen(true)}>
          {t('myFeedback.new')}
        </button>
      </div>

      {all.length > 0 && (
        <div className={styles.filters}>
          <FilterChips
            label={t('myFeedback.kind')}
            value={kind}
            onChange={setKind}
            testId="my-feedback-kinds"
            options={[
              { key: null, label: t('events.all'), count: all.length },
              ...(['DEFECT', 'SUGGESTION'] as const).map((k) => ({
                key: k,
                label: t(`admin.feedback.kinds.${k}`),
                count: all.filter((f) => f.kind === k).length,
              })),
            ]}
          />
          <FilterChips
            label={t('myFeedback.status')}
            value={status}
            onChange={setStatus}
            testId="my-feedback-statuses"
            options={[
              { key: null, label: t('events.all'), count: ofKind.length },
              ...FEEDBACK_STATUSES.map((s) => ({ key: s, label: t(`feedback.status.${s}`), count: ofKind.filter((f) => f.status === s).length })),
            ]}
          />
        </div>
      )}

      <section className={ui.card} data-testid="my-feedback">
        {mine.isError && <ErrorView onRetry={() => void mine.refetch()} />}
        {mine.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {mine.data && all.length === 0 && (
          <div className={styles.empty}>
            <p>{t('myFeedback.none')}</p>
            <button type="button" className={ui.button} disabled={readOnly} onClick={() => setDialogOpen(true)}>
              {t('myFeedback.new')}
            </button>
          </div>
        )}
        {all.length > 0 && shown.length === 0 && <p className={ui.muted}>{t('myFeedback.noneFiltered')}</p>}
        {shown.length > 0 && (
          <table className={`${ui.table} ${styles.table}`}>
            <thead>
              <tr>
                <th className={styles.colDate}>{t('myFeedback.colDate')}</th>
                <th className={styles.colKind}>{t('myFeedback.colKind')}</th>
                <th>{t('myFeedback.colText')}</th>
                <th className={styles.colStatus}>{t('myFeedback.colStatus')}</th>
                <th>{t('myFeedback.colAnswer')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f) => (
                <tr key={f.id} data-testid="my-feedback-item">
                  <td className={styles.nowrap}>{formatDate(f.createdAt, locale)}</td>
                  <td>
                    <span className={`${ui.chip} ${f.kind === 'DEFECT' ? styles.chipDefect : ui.chipSoft}`}>{t(`admin.feedback.kinds1.${f.kind}`)}</span>
                  </td>
                  <td>
                    <p className={styles.body}>{f.body}</p>
                    {f.page && <span className={ui.secondary}>{t('myFeedback.from', { page: f.page })}</span>}
                  </td>
                  <td>
                    <span className={`${ui.chip} ${statusChipClass(f.status)}`}>{t(`feedback.status.${f.status}`)}</span>
                    {f.deletesOn && <span className={ui.secondary}>{t('myFeedback.deletesOn', { date: formatDate(f.deletesOn, locale) })}</span>}
                  </td>
                  <td className={f.adminNote ? styles.answer : ui.muted}>{f.adminNote ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <FeedbackDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
